import { useState, useEffect } from "react";
import { voucherApi } from "@/services/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Download, Search } from "lucide-react";
import ReportLayout from "@/components/reports/ReportLayout";
import ReportSummaryHeader from "@/components/reports/ReportSummaryHeader";
import TablePagination from "@/components/masters/TablePagination";
import {
  exportToExcel,
  formatDateForExcel,
  ExcelColumn,
} from "@/utils/exportUtils";

// Define interfaces for API data
interface Voucher {
  id: string;
  entryNumber: string;
  voucherNumber: string;
  description: string;
  fromAccountId: string;
  fromAccountName?: string;
  fromAccountDescription?: string;
  toAccountId: string;
  toAccountName?: string;
  toAccountDescription?: string;
  amount: number;
  date: string;
  type: "cash" | "bank";
  voucherType: "payment" | "receipt";
}

// Type for voucher fetch params
interface VoucherFetchParams {
  page: number;
  limit: number;
  query?: string;
  type?: string;
  voucherType?: string;
  fromDate?: string;
  toDate?: string;
}

const VoucherReport = () => {
  // State for vouchers data
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDocs, setTotalDocs] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [allTotalAmount, setAllTotalAmount] = useState(0);

  // UI state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    voucherType: "all",
    type: "all",
    query: "",
  });

  const { toast } = useToast();

  // Fetch vouchers from API
  useEffect(() => {
    fetchVouchers();
  }, [currentPage, itemsPerPage, filters]);

  useEffect(() => {
    // Fetch all data for totals
    const fetchAllTotals = async () => {
      try {
        const exportParams: VoucherFetchParams = {
          page: 1,
          limit: "all" as unknown as number,
        };
        if (filters.query) exportParams.query = filters.query.trim();
        if (filters.type !== "all") exportParams.type = filters.type;
        if (filters.voucherType !== "all")
          exportParams.voucherType = filters.voucherType;
        if (filters.fromDate) exportParams.fromDate = filters.fromDate;
        if (filters.toDate) exportParams.toDate = filters.toDate;
        const response = await voucherApi.getVouchers(exportParams);
        const docs = response.docs || [];
        let totalAmount = 0;
        docs.forEach((voucher: Voucher) => {
          totalAmount += voucher.amount;
        });
        setAllTotalAmount(totalAmount);
      } catch {
        setAllTotalAmount(0);
      }
    };
    fetchAllTotals();
    // eslint-disable-next-line
  }, [filters]);

  // Function to fetch vouchers
  const fetchVouchers = async () => {
    try {
      setLoading(true);

      // Prepare filter parameters
      const params: VoucherFetchParams = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add optional filters if they exist
      if (filters.query) params.query = filters.query.trim();
      if (filters.type !== "all") params.type = filters.type;
      if (filters.voucherType !== "all")
        params.voucherType = filters.voucherType;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      console.log("Fetching vouchers with params:", params);
      const response = await voucherApi.getVouchers(params);

      if (response && response.docs) {
        setVouchers(response.docs);
        setTotalDocs(response.totalDocs);
        setHasNextPage(response.hasNextPage);
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to fetch vouchers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to handle filter changes
  const handleFilterChange = (
    filterName: keyof typeof filters,
    value: string
  ) => {
    setFilters({
      ...filters,
      [filterName]: value,
    });

    // Reset to first page when filters change
    setCurrentPage(1);
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      voucherType: "all",
      type: "all",
      query: "",
    });
    setCurrentPage(1);
    fetchVouchers();
  };

  // Apply filters and fetch vouchers
  const applyFilters = () => {
    // Reset to first page when applying filters
    setCurrentPage(1);
    fetchVouchers();
  };

  // Helper function to format date to 'dd-mm-yyyy'
  const formatDateISO = formatDateForExcel;

  // Function to export data to Excel
  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      toast({
        title: "Exporting",
        description: "Preparing your Excel file...",
      });

      // Fetch all data for export
      const exportParams: VoucherFetchParams = {
        page: 1,
        limit: "all" as unknown as number, // API should handle "all" as a special case
      };

      // Add filters
      if (filters.query) exportParams.query = filters.query.trim();
      if (filters.type !== "all") exportParams.type = filters.type;
      if (filters.voucherType !== "all")
        exportParams.voucherType = filters.voucherType;
      if (filters.fromDate) exportParams.fromDate = filters.fromDate;
      if (filters.toDate) exportParams.toDate = filters.toDate;

      const response = await voucherApi.getVouchers(exportParams);
      const dataToExport = response.docs || [];

      // Define columns for Excel export
      const columns: ExcelColumn[] = [
        { header: "SL No", key: "slNo", width: 10 },
        { header: "Date", key: "date", width: 15 },
        { header: "Entry Number", key: "entryNumber", width: 15 },
        { header: "Voucher Number", key: "voucherNumber", width: 20 },
        { header: "Type", key: "type", width: 15 },
        { header: "Amount", key: "amount", width: 15 },
        { header: "Remarks", key: "description", width: 30 },
      ];

      // Prepare data for export
      const formattedData = dataToExport.map(
        (voucher: Voucher, index: number) => ({
          slNo: index + 1,
          date: formatDateISO(voucher.date),
          entryNumber: voucher.entryNumber,
          voucherNumber: voucher.voucherNumber,
          type: `${voucher.voucherType} (${voucher.type})`,
          amount: voucher.amount,
          description: voucher.description,
        })
      );

      // Generate filename with current date
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const filename = `voucher_report_${dateStr}.xlsx`;

      // Use the reusable export utility
      await exportToExcel(formattedData, {
        filename,
        sheetName: "Voucher Report",
        columns,
        styleHeader: true,
        totalRow: { key: "amount", label: "Total" },
      });

      toast({
        title: "Export Successful",
        description: "Your report has been exported to Excel.",
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description:
          error.message || "Failed to export data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate total amount
  const totalAmount = vouchers.reduce(
    (sum, voucher) => sum + voucher.amount,
    0
  );

  return (
    <ReportLayout
      title="Voucher Report"
      description="View and analyze all voucher transactions"
    >
      <Card>
        <CardHeader className="flex flex-row justify-between items-center"></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-xs invisible">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by entry/voucher/description"
                  value={filters.query}
                  onChange={(e) => handleFilterChange("query", e.target.value)}
                  className="pl-8 w-full h-10 placeholder:text-m"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="from-date">From Date</Label>
              <div className="flex items-center relative">
                <Input
                  id="from-date"
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) =>
                    handleFilterChange("fromDate", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="to-date">To Date</Label>
              <div className="flex items-center relative">
                <Input
                  id="to-date"
                  type="date"
                  value={filters.toDate}
                  min={filters.fromDate || undefined}
                  onChange={(e) => handleFilterChange("toDate", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="voucherType">Voucher Type</Label>
              <Select
                value={filters.voucherType}
                onValueChange={(value) =>
                  handleFilterChange("voucherType", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Voucher Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Voucher Types</SelectItem>
                  <SelectItem value="payment">Payment</SelectItem>
                  <SelectItem value="receipt">Receipt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Payment Method</Label>
              <Select
                value={filters.type}
                onValueChange={(value) => handleFilterChange("type", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Payment Methods" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payment Methods</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={resetFilters}>
            Reset
          </Button>
          <Button
            onClick={handleExportToExcel}
            disabled={loading || vouchers.length === 0}
            className="whitespace-nowrap"
          >
            <Download className="mr-2 h-4 w-4" />
            Export to Excel
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"></div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SL No</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Entry Number</TableHead>
                <TableHead>Voucher Number</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="hidden lg:table-cell">Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : vouchers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-6 text-muted-foreground"
                  >
                    {filters.query ||
                    filters.fromDate ||
                    filters.toDate ||
                    filters.voucherType !== "all" ||
                    filters.type !== "all"
                      ? "No vouchers match your search criteria"
                      : "No vouchers found."}
                  </TableCell>
                </TableRow>
              ) : (
                vouchers.map((voucher, index) => (
                  <TableRow key={voucher.id}>
                    <TableCell>
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </TableCell>
                    <TableCell>{formatDateISO(voucher.date)}</TableCell>
                    <TableCell>{voucher.entryNumber}</TableCell>
                    <TableCell>{voucher.voucherNumber}</TableCell>
                    <TableCell>
                      {voucher.voucherType === "payment"
                        ? "Payment"
                        : "Receipt"}{" "}
                      ({voucher.type === "cash" ? "Cash" : "Bank"})
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {voucher.amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell max-w-[200px] truncate">
                      {voucher.description}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {/* Total Row */}
              {!loading && vouchers.length > 0 && (
                <TableRow className="font-bold bg-muted">
                  <TableCell colSpan={5}>Total</TableCell>
                  <TableCell className="text-right font-medium">
                    {allTotalAmount.toLocaleString()}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell"></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalDocs / itemsPerPage)}
              onPageChange={setCurrentPage}
            />
          </div>
        </CardContent>
      </Card>
    </ReportLayout>
  );
};

export default VoucherReport;
